/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import { AccessType, ResponsiveHeroImage, ReviewRating } from "@quintype/components";
import get from "lodash/get";
import PropTypes from "prop-types";
import React from "react";
import { connect } from "react-redux";
import { addTargetBlankToExtLinks } from "../../../../../utils/add-target-blank-to-extlinks.js";
import { breakpoint } from "../../../../../utils/breakpoint";
import { isRelatedStoriesExtraAdSlotEnabled } from "../../../../../utils/relatedStoriesAdSlot.js";
import { accessCheck, altTagOfHeroImages, getSocialShareStyle, selectedStoryTemplate } from "../../../../utils.js";
import { AdService } from "../../../ad-service.js";
import { AmazonPolly } from "../../../amazon-polly/amazon-polly";
import { HeroImageCaption } from "../../../atoms/hero-image-caption/index.js";
import { StoryHeadline } from "../../../atoms/story-headline/index.js";
import { StorySection } from "../../../atoms/story-section/index.js";
import { StorySubheadline } from "../../../atoms/story-subheadline/index.js";
import { Author } from "../../../basic/author.js";
import { ImageFallback } from "../../../basic/image-fallback.js";
import { Share } from "../../../molecules/share/index.js";
import { StoryFooter } from "../../../molecules/story-footer";
import { checkIfStoryPublic, getAccessData, getStoryCards, handleAccess } from "../../../sso-components/sso-utils.js";
import { RelatedStories } from "../../../story-page-components/related-stories.js";
import { StoryTags } from "../../../story-page-components/story-tags.js";
import {
  supportAmazonPollyGrid,
  supportAuthorGrid,
  supportCaptionGrid,
  supportHeadlineGrid,
  supportHorizontalAuthorGrid,
  supportHorizontalCaptionGrid,
  supportHorizontalGrid,
  supportHorizontalLineGrid,
  supportHorizontalShareGrid,
  supportHorizontalTagMetypeGrid,
  supportImageGrid,
  supportMetypeGrid,
  supportRelatedStoryGrid,
  supportReviewRatingGrid,
  supportSectionGrid,
  supportSocialShareGrid,
  supportStoryTagGrid,
  supportSummaryGrid
} from "./story-selectors.js";
import "./text-story.m.css";

function getTextStoryClass(textStory) {
  switch (textStory) {
    case "hero-priority":
      return "photo-priority-temp";
    case "hero-overlay":
      return "hero-overlay-temp";
    case "headline-hero-priority":
      return "headline-hero-priority-temp";
    case "headline-sideway":
      return "headline-sideway-temp";
    case "headline-priority":
      return "headline-priority-temp";
    default:
      return "";
  }
}

const GetSideAd = ({ textStory, id = "Story-Mrec-Text" }) => {
  switch (textStory) {
    case "hero-priority":
      return null;
    case "hero-overlay":
      return null;
    case "headline-hero-priority":
      return null;
    case "headline-sideway":
      return null;
    default:
      return (
        <div className="app-ad app-ad--story-mrec">
          <AdService adtype="Story-Mrec" slotId={id} shouldRefreshOnce />
        </div>
      );
  }
};

GetSideAd.propTypes = {
  id: PropTypes.string,
  textStory: PropTypes.string
};

function getAspectRatio(textStory) {
  switch (textStory) {
    case "hero-priority":
      return [16, 9];
    case "hero-overlay":
      return [16, 9];
    case "headline-hero-priority":
      return [16, 9];
    case "headline-sideway":
      return [4, 3];
    case "headline-priority":
      return [16, 9];
    default:
      return [8, 3];
  }
}
function mobileImageRatio(textStory) {
  switch (textStory) {
    case "headline-sideway":
      return [4, 3];
    default:
      return [16, 9];
  }
}

function TextStoryTemplate(props) {
  const isReviewStory = props.story["story-template"] === "review";
  const haveFocusPoint = !!get(props, ["story", "hero-image-metadata", "focus-point"]);
  const socialShareStyle = getSocialShareStyle(props.config);
  const textStory = selectedStoryTemplate("text-story", props.story, props.config);
  const readTime = get(props, ["story", "read-time"]);
  const imageElement = breakpoint("tablet") ? (
    <ResponsiveHeroImage
      story={props.story}
      aspectRatio={mobileImageRatio(textStory)}
      alt={altTagOfHeroImages(props.story)}
      defaultWidth={480}
      widths={[250, 480, 640]}
      sizes="(max-width: 500px) 98%, (max-width: 768px) 48%, 98%"
      imgParams={{ auto: ["format", "compress"], fit: "max" }}
    />
  ) : (
    <ResponsiveHeroImage
      story={props.story}
      aspectRatio={getAspectRatio(textStory)}
      alt={altTagOfHeroImages(props.story)}
      defaultWidth={480}
      widths={[250, 480, 640, 960, 1200]}
      sizes="(max-width: 500px) 98%"
      imgParams={{ auto: ["format", "compress"], fit: "max" }}
    />
  );
  const publishedTime = props.story["last-published-at"]
    ? props.story["last-published-at"]
    : props.story["first-published-at"];
  const authorsArray = get(props.story, ["authors"])
    ? get(props.story, ["authors"])
    : [
        {
          name: props.story["author-name"],
          id: props.story["author-id"]
        }
      ];

  const textStoryList = ["hero-priority", "headline-hero-priority", "headline-priority"];
  const isHorizontal = !isReviewStory && textStoryList.includes(textStory) && socialShareStyle.includes("horizontal");
  const storyAccess = get(props, ["access"]);
  const accessLoading = get(props, ["accessLoading"]);
  const cdnImage = get(props, ["config", "cdn-image"], "");
  const storyCards = get(props, ["story", "cards"], []);
  const isStoryPublic = checkIfStoryPublic(props.story);
  const storyId = get(props, ["story", "id"]);
  let showPolly = get(props, ["access", storyId, "granted"], false);

  // Need to remove this once amazon-polly is properly supported in PB story-layouts
  const pbShowPolly = get(props.config, ["theme-attributes", "pageBuilderConfig", "amazonPolly"]);
  if (pbShowPolly) showPolly = pbShowPolly;

  function getSocialShare(textStory) {
    const isReviewStory = get(props, ["story", "story-template"], "") === "review";
    if (isReviewStory) {
      return <Share story={props.story} config={props.config} grey={socialShareStyle.includes("grey")} />;
    }

    switch (textStory) {
      case "hero-priority":
        return (
          <Share
            story={props.story}
            config={props.config}
            grey={socialShareStyle.includes("grey")}
            vertical={socialShareStyle.includes("vertical")}
          />
        );
      case "hero-overlay":
        return <Share story={props.story} config={props.config} grey={socialShareStyle.includes("grey")} vertical />;
      case "headline-hero-priority":
        return (
          <Share
            story={props.story}
            config={props.config}
            grey={socialShareStyle.includes("grey")}
            vertical={socialShareStyle.includes("vertical")}
          />
        );
      case "headline-priority":
        return (
          <Share
            story={props.story}
            config={props.config}
            grey={socialShareStyle.includes("grey")}
            vertical={socialShareStyle.includes("vertical")}
          />
        );
      case "headline-sideway":
        return <Share story={props.story} config={props.config} grey={socialShareStyle.includes("grey")} vertical />;
      default:
        return <Share story={props.story} config={props.config} grey={socialShareStyle.includes("grey")} />;
    }
  }

  function getImageTemplate(props) {
    const image = get(props.story, ["hero-image-s3-key"]);
    if (image) {
      return <figure styleName={`image-wrapper ${!haveFocusPoint ? "full-height" : ""}`}>{imageElement}</figure>;
    } else if (textStory === "hero-overlay" && !image) {
      return (
        <div styleName="fallback-image">
          <ImageFallback />
        </div>
      );
    } else {
      return <div />;
    }
  }

  const textStoryClassMappings = {
    "hero-overlay": "simple"
  };

  function link() {
    const section = textStoryClassMappings[textStory] || "";
    const isReviewStory = get(props, ["story", "story-template"], "") === "review";
    if (isReviewStory) {
      return <StorySection story={props.story} config={props.config} />;
    } else {
      return <StorySection story={props.story} config={props.config} template={section} />;
    }
  }

  function headline() {
    const isReviewStory = get(props, ["story", "story-template"], "") === "review";
    if (isReviewStory) {
      return <StoryHeadline headline={props.story.headline} />;
    } else {
      return <StoryHeadline headline={props.story.headline} headlineWhite={textStory === "hero-overlay"} />;
    }
  }

  function subHeadline() {
    const isReviewStory = get(props, ["story", "story-template"], "") === "review";
    if (isReviewStory) {
      return <StorySubheadline subheadline={props.story.subheadline} />;
    } else {
      return <StorySubheadline subheadline={props.story.subheadline} subheadlineWhite={textStory === "hero-overlay"} />;
    }
  }

  function getAuthor(props) {
    const isReviewStory = get(props, ["story", "story-template"], "") === "review";
    if (isReviewStory) {
      return (
        <Author
          config={props.config}
          authors={authorsArray}
          isLink
          showDateOnly
          date={publishedTime}
          readTime={readTime}
        />
      );
    }

    switch (textStory) {
      case "hero-priority":
        return (
          <Author
            config={props.config}
            authors={authorsArray}
            isLink
            showDateOnly
            date={publishedTime}
            story={textStory}
            readTime={readTime}
          />
        );
      case "hero-overlay":
        return (
          <Author
            textStory={textStory}
            config={props.config}
            authors={authorsArray}
            isLink
            showDateOnly
            date={publishedTime}
            story={textStory}
            readTime={readTime}
          />
        );
      default:
        return (
          <Author
            config={props.config}
            authors={authorsArray}
            isLink
            showDateOnly
            date={publishedTime}
            readTime={readTime}
          />
        );
    }
  }

  const gridImageStyle = supportImageGrid[textStory] ? supportImageGrid[textStory] : supportImageGrid["default"];

  const gridCaptionStyle = supportCaptionGrid[textStory]
    ? supportCaptionGrid[textStory]
    : supportCaptionGrid["default"];

  const gridHorizontalCaptionStyle = supportHorizontalCaptionGrid[textStory]
    ? supportHorizontalCaptionGrid[textStory]
    : supportCaptionGrid[textStory];

  const gridSectionStyle = supportSectionGrid[textStory]
    ? supportSectionGrid[textStory]
    : supportSectionGrid["default"];

  const gridHorizontalSectionStyle = supportHorizontalGrid[textStory]
    ? supportHorizontalGrid[textStory]
    : supportSectionGrid[textStory];

  const gridHeadlineStyle = supportHeadlineGrid[textStory]
    ? supportHeadlineGrid[textStory]
    : supportHeadlineGrid["default"];

  const gridHorizontalHeadlineStyle = supportHorizontalGrid[textStory]
    ? supportHorizontalGrid[textStory]
    : supportHeadlineGrid[textStory];

  const gridSummaryStyle = supportSummaryGrid[textStory]
    ? supportSummaryGrid[textStory]
    : supportSummaryGrid["default"];

  const gridHorizontalSummaryStyle = supportHorizontalGrid[textStory]
    ? supportHorizontalGrid[textStory]
    : supportSummaryGrid[textStory];

  const gridAmazonPollyStyle = supportAmazonPollyGrid[textStory]
    ? supportAmazonPollyGrid[textStory]
    : supportAmazonPollyGrid["default"];

  const gridHorizontalAmazonPollyStyle = supportHorizontalGrid[textStory]
    ? supportHorizontalGrid[textStory]
    : supportAmazonPollyGrid[textStory];

  const gridAuthorStyle = supportAuthorGrid[textStory] ? supportAuthorGrid[textStory] : supportAuthorGrid["default"];

  const gridHorizontalAuthorStyle = supportHorizontalAuthorGrid[textStory]
    ? supportHorizontalAuthorGrid[textStory]
    : "";

  const gridSocialShareStyle = supportSocialShareGrid[textStory]
    ? supportSocialShareGrid[textStory]
    : supportSocialShareGrid["default"];

  const gridHorizontalShareStyle = supportHorizontalShareGrid[textStory] ? supportHorizontalShareGrid[textStory] : "";

  const gridHorizontalLineStyle = supportHorizontalLineGrid[textStory]
    ? supportHorizontalLineGrid[textStory]
    : supportHorizontalLineGrid["default"];

  const gridReviewRatingStyle = supportReviewRatingGrid[textStory]
    ? supportReviewRatingGrid[textStory]
    : supportReviewRatingGrid["default"];

  const gridStoryTagStyle = supportStoryTagGrid[textStory]
    ? supportStoryTagGrid[textStory]
    : supportStoryTagGrid["default"];

  const gridRelatedStoryStyle = supportRelatedStoryGrid[textStory]
    ? supportRelatedStoryGrid[textStory]
    : supportRelatedStoryGrid["default"];

  const gridMetypeStyle = supportMetypeGrid[textStory] ? supportMetypeGrid[textStory] : supportMetypeGrid["default"];

  const gridHorizontalMetypeStyle = supportHorizontalTagMetypeGrid[textStory]
    ? supportHorizontalTagMetypeGrid[textStory]
    : supportMetypeGrid[textStory];

  const gridHorizontalStoryTagStyle = supportHorizontalTagMetypeGrid[textStory]
    ? supportHorizontalTagMetypeGrid[textStory]
    : supportStoryTagGrid[textStory];
  return (
    <div
      data-testid="text-story-grid"
      className="blank-story grid"
      styleName={`text-story-grid ${isHorizontal ? "horizontal" : ""}  ${
        !isReviewStory ? getTextStoryClass(textStory) : ""
      }`}
    >
      {/* hero image */}
      <div data-testid="text-story-image" className={gridImageStyle} styleName="image">
        {getImageTemplate(props)}
      </div>
      <div styleName="caption" className={isHorizontal ? gridHorizontalCaptionStyle : gridCaptionStyle}>
        <HeroImageCaption story={props.story} />
      </div>
      {/* Section-name */}
      <div
        styleName="item section-name item-overlay"
        className={isHorizontal ? gridHorizontalSectionStyle : gridSectionStyle}
      >
        {link()}
      </div>
      {/* Headline */}
      <div
        styleName="item  story-headline item-overlay"
        className={isHorizontal ? gridHorizontalHeadlineStyle : gridHeadlineStyle}
      >
        {headline()}
      </div>
      {/* Summary */}
      <p
        styleName="item story-summary item-overlay"
        className={isHorizontal ? gridHorizontalSummaryStyle : gridSummaryStyle}
      >
        {subHeadline()}
      </p>
      {/* Amazon Polly */}
      {showPolly && (
        <div
          styleName="item amazon-polly-wrapper"
          className={isHorizontal ? gridHorizontalAmazonPollyStyle : gridAmazonPollyStyle}
        >
          <AmazonPolly story={props.story} customClass="amazon-polly-custom-selector" />
        </div>
      )}
      {/* Author */}
      <div
        data-testid="text-story-author"
        styleName="item author item-overlay"
        className={isHorizontal ? gridHorizontalAuthorStyle : gridAuthorStyle}
      >
        {getAuthor(props)}
      </div>
      {/* Social-share */}
      <div
        data-testid="text-story-share"
        styleName="item social-share"
        className={isHorizontal ? gridHorizontalShareStyle : gridSocialShareStyle}
      >
        <div styleName="sticky">{getSocialShare(textStory)}</div>
      </div>
      {/* Horizontal Line */}
      <div data-testid="text-story-horizontal-line" styleName="horizontal-line" className={gridHorizontalLineStyle} />
      {/* ReviewRating */}
      {isReviewStory && (
        <div
          styleName="item rating-wrapper"
          data-test-id="rating-wrapper"
          className={isHorizontal ? gridHorizontalShareStyle : gridReviewRatingStyle}
        >
          <h5 className="rating__title">
            {get(props.story, ["metadata", "review-title"])}{" "}
            <span className="rating__value" data-test-id="rating-value">
              ({get(props.story, ["metadata", "review-rating", "value"], 0)} / 5)
            </span>
          </h5>
          <div className="rating">
            <ReviewRating value={get(props.story, ["metadata", "review-rating", "value"], "0")} />
          </div>
        </div>
      )}
      {/* Story-cards */}
      {isStoryPublic
        ? getStoryCards(storyCards, props.config, props.story, cdnImage, props.getStoryCardTemplate, props.firstStory)
        : handleAccess(
            props.story,
            storyAccess,
            accessLoading,
            props.config,
            props.getStoryCardTemplate,
            props.firstStory
          )}
      {/* StoryTags */}
      <div
        data-testid="text-story-tags"
        styleName="item"
        className={isHorizontal ? gridHorizontalStoryTagStyle : gridStoryTagStyle}
      >
        <StoryTags tags={props.story.tags} config={props.config} />
      </div>
      {/* Related Stories */}
      <div data-testid="related-text-story" styleName="item related-story" className={gridRelatedStoryStyle}>
        <div>{<GetSideAd textStory={textStory} id={`Story-Mrec-Text-${props.index}`} />}</div>
        {isRelatedStoriesExtraAdSlotEnabled(props.publisherConfig, props.config) && (
          <div>
            <GetSideAd textStory={textStory} id={`Story-Mrec-Text-2-${props.index}`} />
          </div>
        )}
        <RelatedStories
          story={props.story}
          config={props.config}
          template={textStory}
          wide
          staticRelatedStories={props.staticRelatedStories}
        />
      </div>
      {/* Metype + Ad */}
      <div data-testid="text-story-metype" className={isHorizontal ? gridHorizontalMetypeStyle : gridMetypeStyle}>
        {!props.preview && <StoryFooter story={props.story} />}
      </div>
    </div>
  );
}

TextStoryTemplate.propTypes = {
  story: PropTypes.object,
  config: PropTypes.object,
  preview: PropTypes.bool,
  firstStory: PropTypes.bool,
  getStoryCardTemplate: PropTypes.func,
  staticRelatedStories: PropTypes.array,
  publisherConfig: PropTypes.object,
  index: PropTypes.number
};

class TextStoryAccessWrapper extends React.Component {
  componentDidMount() {
    const { checkAccess, story, accessUpdated, accessIsLoading, config } = this.props;
    const getAccess = () => getAccessData(checkAccess, story, accessUpdated, accessIsLoading, config);
    this.props.initAccessType(getAccess);
    addTargetBlankToExtLinks(this.storyWrapperElement);
  }

  render() {
    const storyMetadata = get(this.props, ["story", "metadata"], {});
    return (
      <div className={`story-grid`} ref={el => (this.storyWrapperElement = el)}>
        <div style={{ display: "none" }} className="blank-story-metadata">
          {JSON.stringify(storyMetadata)}
        </div>
        {/* DO NOT DELETE, HACK ON PURPOSE. */}
        <TextStoryTemplate {...this.props} />
      </div>
    );
  }
}

TextStoryAccessWrapper.propTypes = {
  story: PropTypes.object,
  config: PropTypes.object,
  preview: PropTypes.bool,
  access: PropTypes.object,
  checkAccess: PropTypes.func,
  initAccessType: PropTypes.func,
  accessUpdated: PropTypes.func,
  accessIsLoading: PropTypes.func
};

class TextStoryBase extends React.Component {
  render() {
    const email = get(this.props, ["member", "email"]);
    const phone = get(this.props, ["member", "metadata", "phone-number"], "");

    const themeAttributes = get(this.props, ["config", "theme-attributes"]);
    const PbAccessTypeConfig = get(this.props, ["config", "pagebuilder-config", "general", "accesstypeIntegration"]);
    const accessTypeKey = get(PbAccessTypeConfig, ["accesstypeKey"], get(themeAttributes, ["accesstypeKey"]));
    const accessTypeBkIntegrationId = get(
      PbAccessTypeConfig,
      ["bridgeKeeperIntegrationId"],
      get(themeAttributes, ["accesstypeBkIntegrationId"], "")
    );
    const isStaging =
      get(PbAccessTypeConfig, ["accesstypeMode"]) === "staging" ||
      get(themeAttributes, ["accesstypeEnvironment"]) === "staging";
    const enableAccesstype = get(
      PbAccessTypeConfig,
      ["enableAccesstype"],
      get(themeAttributes, ["enableAccesstype"], false)
    );
    const disableMetering = get(
      PbAccessTypeConfig,
      ["disableMetering"],
      get(themeAttributes, ["disableMetering"]) === "true"
    );

    const storyId = get(this.props, ["story", "id"]);
    return (
      <AccessType
        enableAccesstype={accessCheck(enableAccesstype)}
        isStaging={isStaging}
        accessTypeKey={accessTypeKey} // "9rm1ijqQmGfJ2kwQ9zt2gWhK"
        accessTypeBkIntegrationId={accessTypeBkIntegrationId}
        email={email}
        phone={phone}
        disableMetering={disableMetering}
        storyId={storyId}
      >
        {({ initAccessType, checkAccess, accessUpdated, accessIsLoading }) => (
          <div className={`story-grid`} ref={el => (this.storyWrapperElement = el)}>
            <TextStoryAccessWrapper
              accessIsLoading={accessIsLoading}
              accessUpdated={accessUpdated}
              initAccessType={initAccessType}
              checkAccess={checkAccess}
              {...this.props}
            />
          </div>
        )}
      </AccessType>
    );
  }
}

const mapStateToProps = state => ({
  config: get(state, ["qt", "config"], {}),
  member: get(state, ["member"], null),
  access: get(state, ["access"], null),
  accessLoading: get(state, ["accessLoading"], true),
  publisherConfig: get(state, ["qt", "data", "publisherConfig"], {})
});

export default connect(mapStateToProps)(TextStoryBase);
